/** Rooms, finish keys and room groups, read from the estimate export. */
import { EstimateData } from "../../interfaces/EstimateData";
import { LineItem } from "../../interfaces/LineItem";
import { FinishKey, RoomGroup } from "../../interfaces/Room";
import { Room, RoomFixture } from "../../interfaces/Room";

type ExportKey = Omit<FinishKey, "line_item"> & { line_item: string | null };
type ExportLineItem = LineItem & { description?: string; unit_cost?: number };

const withoutDuplicateFixtures = (fixtures: RoomFixture[]): RoomFixture[] => {
  const seen = new Set<string>();
  return fixtures.filter((fixture) => {
    if (seen.has(fixture.id)) return false;
    seen.add(fixture.id);
    return true;
  });
};

export const getEstimateRooms = (estimateData: EstimateData): Room[] =>
  (estimateData.rooms ?? []).map((room) =>
    room.extras?.fixtures
      ? { ...room, extras: { ...room.extras, fixtures: withoutDuplicateFixtures(room.extras.fixtures) } }
      : room
  );

export const getEstimateFinishKeys = (estimateData: EstimateData): FinishKey[] => {
  const lineItemsById = new Map((estimateData.line_items ?? []).map((item) => [item.id, item as ExportLineItem]));
  return ((estimateData.keys ?? []) as ExportKey[]).map((key) => {
    const lineItem = key.line_item ? lineItemsById.get(key.line_item) : undefined;
    return {
      ...key,
      line_item: (lineItem
        ? {
            id: lineItem.id,
            name: lineItem.name,
            description: lineItem.description ?? "",
            uom: lineItem.uom,
            unit_cost: lineItem.unit_cost ?? 0,
            total_uc: lineItem.total_uc ?? 0,
          }
        : null) as FinishKey["line_item"],
    };
  });
};

export const getEstimateRoomGroups = (estimateData: EstimateData): RoomGroup[] =>
  (estimateData.room_groups ?? []) as RoomGroup[];
